// Reports: every project's dated history of client reports, newest day first.
// A nightly job drafts one per active, at-risk or blocked project; the
// project's manager (or owner/admin) edits and approves it, then sends it on
// purpose: Send shows who receives it (the project's list, editable there) and
// emails it through report-mail (DESIGN-SYNC "Reports — Merge"; Q59–Q62).
import { useEffect, useReducer, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Icon, Mono, Select } from '../components/primitives';
import { EmptyState, InlineStatus, LoadError, useToast } from '../components/feedback';
import { Field, TextArea } from '../components/field';
import { Dialog } from '../components/dialog';
import { useAuth } from '../lib/auth';
import { can, canOpen } from '../lib/roles';
import { fmtDayKey } from '../lib/dates';
import {
  MAX_RECIPIENTS,
  getReportRecipients,
  listClientReports,
  REPORT_STATUS,
  parseEmails,
  reviewClientReport,
  saveReportRecipients,
  sendClientReport,
  type ClientReport,
} from '../lib/clientReports';

const filter = { flex: '1 1 160px', maxWidth: 240 };

const uniq = (xs: (string | null)[]) => [...new Set(xs.filter((x): x is string => !!x))].sort();

export function ReportsScreen() {
  const { session, perms } = useAuth();
  const uid = session?.user.id;
  const toast = useToast();
  const [reports, setReports] = useState<(ClientReport[] & { failed?: true }) | null>(null);
  const [tries, retry] = useReducer((n: number) => n + 1, 0);
  const [project, setProject] = useState('');
  const [client, setClient] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [sending, setSending] = useState<ClientReport | null>(null);
  // After a send, look again while report-mail does its work.
  const later = useRef<number[]>([]);
  useEffect(() => () => later.current.forEach(clearTimeout), []);

  useEffect(() => {
    let active = true;
    void listClientReports().then((r) => active && setReports(r));
    return () => {
      active = false;
    };
  }, [tries]);

  const all = reports ?? [];
  const projects = [...new Map(all.map((r) => [r.projectId, r.projectName])).entries()].sort(
    (a, b) => a[1].localeCompare(b[1]),
  );
  const clients = uniq(all.map((r) => r.client));
  const shown = all
    .filter((r) => (!project || r.projectId === project) && (!client || r.client === client))
    .sort(
      (a, b) =>
        b.reportDate.localeCompare(a.reportDate) || a.projectName.localeCompare(b.projectName),
    );
  const groups: { date: string; items: ClientReport[] }[] = [];
  for (const r of shown) {
    const last = groups[groups.length - 1];
    if (last?.date === r.reportDate) last.items.push(r);
    else groups.push({ date: r.reportDate, items: [r] });
  }

  const manages = (r: ClientReport) =>
    can(perms, 'pipeline', 'full') || (!!uid && r.managerId === uid);
  const canReview = (r: ClientReport) => r.status === 'draft' && manages(r);
  const canSend = (r: ClientReport) => r.status === 'approved' && manages(r);
  const canOpenProject = canOpen(perms, 'projects');

  const review = async (r: ClientReport, change: { summary?: string; approve?: boolean }) => {
    setBusy(r.id);
    const { error } = await reviewClientReport(r.id, change);
    setBusy(null);
    if (error) {
      toast.push({
        tone: 'error',
        message: `Couldn’t ${change.approve ? 'approve' : 'save'} the ${r.projectName} report: ${error.message}`,
      });
      return;
    }
    setEditing(null);
    toast.push({
      tone: 'success',
      message: change.approve ? `${r.projectName} report approved.` : 'Draft saved.',
    });
    retry();
  };

  const sent = (r: ClientReport) => {
    setSending(null);
    toast.push({ tone: 'success', message: `Sending the ${r.projectName} report.` });
    retry();
    later.current.push(window.setTimeout(retry, 4000), window.setTimeout(retry, 15000));
  };

  return (
    <div
      style={{
        padding: 'var(--hf-space-5)',
        maxWidth: 820,
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
        boxSizing: 'border-box',
      }}
    >
      <p style={{ fontSize: 13, color: 'var(--hf-ink-2)', margin: 0, lineHeight: 1.55 }}>
        Every night Hyprflow writes an automatic draft for each active, at-risk or blocked project.
        The project’s manager edits a draft, approves it, then sends it to the client by email.
        Nothing reaches a client until someone presses Send.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-2)' }}>
        <div style={filter}>
          <Select
            ariaLabel="Project"
            value={project}
            onChange={setProject}
            options={[
              { value: '', label: 'All projects' },
              ...projects.map(([value, label]) => ({ value, label })),
            ]}
          />
        </div>
        <div style={filter}>
          <Select
            ariaLabel="Client"
            value={client}
            onChange={setClient}
            options={[
              { value: '', label: 'All clients' },
              ...clients.map((c) => ({ value: c, label: c })),
            ]}
          />
        </div>
      </div>

      {reports?.failed ? (
        <LoadError what="reports" onRetry={retry} />
      ) : !reports ? (
        <div style={{ fontSize: 13, color: 'var(--hf-ink-3)' }}>Loading…</div>
      ) : groups.length === 0 ? (
        <EmptyState title={all.length ? 'No reports match these filters' : 'No reports yet'}>
          {all.length
            ? 'Choose another project or client.'
            : 'Tonight’s drafts for your active projects will appear here.'}
        </EmptyState>
      ) : (
        groups.map((g) => (
          <section key={g.date} aria-labelledby={`day-${g.date}`}>
            <h2
              id={`day-${g.date}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hf-space-2)',
                margin: '0 0 var(--hf-space-2)',
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--hf-ink-2)',
              }}
            >
              <Icon name="calendar" size={13} style={{ color: 'var(--hf-ink-3)' }} />
              {fmtDayKey(g.date, { weekday: true })}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
              {g.items.map((r) => {
                const st = REPORT_STATUS[r.status] ?? REPORT_STATUS.draft;
                const isEditing = editing?.id === r.id;
                return (
                  <article
                    key={r.id}
                    className="hf-glass"
                    aria-label={`${r.projectName}, ${st.label}`}
                    style={{ padding: '14px 16px', borderRadius: 'var(--hf-radius-card)' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 'var(--hf-space-2)',
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--hf-ink)' }}>
                        {r.projectName}
                      </span>
                      {r.projectCode && <Mono dim>{r.projectCode}</Mono>}
                      {r.client && (
                        <span style={{ fontSize: 12, color: 'var(--hf-ink-3)' }}>· {r.client}</span>
                      )}
                      <span style={{ flex: 1 }} />
                      <Badge tone={st.tone} dot>
                        {st.label}
                      </Badge>
                      {canOpenProject && (
                        <Link
                          to={`/project?id=${r.projectId}`}
                          aria-label={`Open ${r.projectName}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 12,
                            color: 'var(--hf-accent-text)',
                            textDecoration: 'none',
                          }}
                        >
                          Open <Icon name="arrowRight" size={12} />
                        </Link>
                      )}
                    </div>
                    {isEditing ? (
                      <TextArea
                        aria-label={`${r.projectName} report text`}
                        value={editing.text}
                        onChange={(text) => setEditing({ id: r.id, text })}
                        autoResize
                      />
                    ) : (
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          color: 'var(--hf-ink)',
                          lineHeight: 1.55,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {r.summary}
                      </p>
                    )}
                    {r.status === 'approved' && r.sendError && (
                      <div style={{ marginTop: 'var(--hf-space-3)' }}>
                        <InlineStatus tone="error">
                          The last send didn’t go out: {r.sendError}
                        </InlineStatus>
                      </div>
                    )}
                    {canSend(r) && (
                      <div style={{ marginTop: 'var(--hf-space-3)' }}>
                        <Button size="sm" variant="primary" onClick={() => setSending(r)}>
                          Send
                        </Button>
                      </div>
                    )}
                    {canReview(r) && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 'var(--hf-space-2)',
                          marginTop: 'var(--hf-space-3)',
                        }}
                      >
                        {isEditing ? (
                          <>
                            <Button
                              size="sm"
                              variant="primary"
                              loading={busy === r.id}
                              disabled={!editing.text.trim()}
                              onClick={() => void review(r, { summary: editing.text })}
                            >
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              icon="check"
                              loading={busy === r.id}
                              onClick={() => void review(r, { approve: true })}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy === r.id}
                              onClick={() => setEditing({ id: r.id, text: r.summary })}
                            >
                              Edit
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ))
      )}
      {sending && <SendDialog report={sending} onClose={() => setSending(null)} onSent={sent} />}
    </div>
  );
}

/**
 * Send, on purpose: who receives it (the project's list, saved for its next
 * reports too, seen only by its manager and owner/admin), then Send to N.
 * send_client_report refuses an empty list, a draft and anyone else.
 */
function SendDialog({
  report: r,
  onClose,
  onSent,
}: {
  report: ClientReport;
  onClose: () => void;
  onSent: (r: ClientReport) => void;
}) {
  const [saved, setSaved] = useState<string[] | null | undefined>(undefined);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void getReportRecipients(r.projectId).then((list) => {
      if (!active) return;
      setSaved(list);
      if (list) setText(list.join('\n'));
    });
    return () => {
      active = false;
    };
  }, [r.projectId]);

  const { emails, bad } = parseEmails(text);
  const changed = !!saved && emails.join() !== saved.join();
  const problem = bad.length
    ? `Not an email address: ${bad.join(', ')}`
    : emails.length > MAX_RECIPIENTS
      ? `At most ${MAX_RECIPIENTS} people.`
      : '';

  const send = async () => {
    setBusy(true);
    setError('');
    const saving = changed ? await saveReportRecipients(r.projectId, emails) : { error: null };
    const { error: e } = saving.error ? saving : await sendClientReport(r.id);
    setBusy(false);
    if (e) setError(e.message);
    else onSent(r);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Send the ${r.projectName} report`}
      dirty={changed}
      footer={(close) => (
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!saved || !emails.length || !!problem}
            onClick={() => void send()}
          >
            {emails.length === 1 ? 'Send to 1 person' : `Send to ${emails.length} people`}
          </Button>
        </>
      )}
    >
      {saved === undefined ? (
        <div style={{ fontSize: 13, color: 'var(--hf-ink-3)' }}>Loading…</div>
      ) : saved === null ? (
        <InlineStatus tone="error">Couldn’t load who receives this project’s reports.</InlineStatus>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--hf-ink-2)', lineHeight: 1.55 }}>
            This emails the {fmtDayKey(r.reportDate)} report, as approved, from Hyprflow. It can’t
            be unsent.
          </p>
          <Field
            label="Send to"
            hint="One email address per line. Kept for this project’s next reports; only its manager, owner and admin see it."
            error={problem || undefined}
          >
            <TextArea value={text} onChange={setText} placeholder="name@client.com" autoResize />
          </Field>
          {error && <InlineStatus tone="error">{error}</InlineStatus>}
        </div>
      )}
    </Dialog>
  );
}
